import DuplicateStareCivilaForm from '@/components/DuplicateStareCivilaForm'
import DynamicForm from '@/components/DynamicForm'
import ProgramareCasatorie from '@/components/ProgramareCasatorie'
import LoginForm from '@/components/auth/LoginForm'
import {
  Breadcrumbs,
  Button,
  Footer,
  H1,
  Header,
  Main,
  Page,
  Wrap,
} from '@/design-system'
import {
  CreateBookingAppointmentInput,
  MenuItems,
  ServiceStep,
} from '@/generated/graphql'
import { useAuth } from '@/lib/auth'
import { handleHtmlNode } from '@/lib/html'
import { sdk } from '@/lib/sdk'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/outline'
import {
  add,
  addDays,
  eachDayOfInterval,
  endOfMonth,
  format,
  getDay,
  isBefore,
  isSameDay,
  isSameMonth,
  parse,
  parseISO,
  setDefaultOptions,
  startOfToday,
} from 'date-fns'
import { ro } from 'date-fns/locale'
import Link from 'next/link'
import Script from 'next/script'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import ReactHtmlParser from 'react-html-parser'
import useSWR, { preload } from 'swr'

setDefaultOptions({ locale: ro })

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export async function getStaticProps({ params: { serviceSlug, stepSlug } }) {
  const menu = (await sdk().menuItems()).menu_items

  const step = (await sdk().stepBySlug({ serviceSlug, stepSlug }))
    .service_step[0]

  if (!step) {
    console.error('*** Step not found', serviceSlug, stepSlug)
    return {
      notFound: true,
    }
  }

  let papersSubmissionStep = null

  if (serviceSlug === 'casatorie') {
    const papersStep = (
      await sdk().stepBySlug({
        serviceSlug,
        stepSlug: 'programare-depunere',
      })
    ).service_step[0]

    if (papersStep) {
      papersSubmissionStep = papersStep
    }
  }

  return {
    props: { menu, step, papersSubmissionStep },
    revalidate: 60,
  }
}

const holidays = [
  [1, 24],
  [4, 14],
  [4, 17],
  [5, 1],
  [6, 1],
  [6, 2],
  [6, 5],
  [8, 14],
  [8, 15],
  [11, 30],
  [12, 1],
  [12, 25],
  [12, 26],
]

const isHoliday = (day: Date) => {
  // console.log('isHoliday', day, holidays)
  for (const h of holidays) {
    // console.log(h[0], h[1], day.getMonth(), day.getDate())
    if (h[0] === day.getMonth() + 1 && h[1] === day.getDate()) return true
  }

  return false
}

const getNextDays = (day) => {
  // console.log('next days', day)
  const res = []
  for (let i = 10; i < 31; i++) {
    const d = addDays(day, i)
    // console.log(d.getDay(), d)
    if (d.getDay() > 3 && d.getDay() < 7) {
      res.push(d)
    }
  }

  return res
}

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

const fetcher = (url) => fetch(url).then((r) => r.json())

// Map service slugs to their corresponding static forms
const staticFormComponents = {
  'duplicate-stare-civila': DuplicateStareCivilaForm,
}

export default function Programari({
  menu,
  step,
  papersSubmissionStep,
}: {
  menu: MenuItems[]
  step: ServiceStep
  papersSubmissionStep?: ServiceStep
}) {
  if (step.service.slug === 'casatorie') {
    return (
      <ProgramareCasatorie
        menu={menu}
        step={step}
        papersSubmissionStep={papersSubmissionStep}
      />
    )
  }
  const [today, setToday] = useState(null)
  const [months, setMonths] = useState({})
  const [isEditing, setIsEditing] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [createdAppointment, setCreatedAppointment] = useState(null)
  let [selectedDay, setSelectedDay] = useState(startOfToday())
  let [currentMonth, setCurrentMonth] = useState(
    format(startOfToday(), 'MMM-yyyy')
  )
  let firstDayCurrentMonth = parse(currentMonth, 'MMM-yyyy', new Date())
  const year = format(firstDayCurrentMonth, 'yyyy')
  const month = format(firstDayCurrentMonth, 'MM')
  const [globalError, setGlobalError] = useState(null)
  const [nextSlots, setNextSlots] = useState(null)
  let nextAvailableInterval = null

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm()

  const { isAuthenticated, setIsAuthenticated, profile } = useAuth([
    'first_name',
    'last_name',
    'email',
    'phone_number',
  ])

  const { data: slots, error: slotsError } = useSWR(
    `/api/booking/slots/${step.id}/${year}/${month}`,
    fetcher
  )

  let days = eachDayOfInterval({
    start: firstDayCurrentMonth,
    end: endOfMonth(firstDayCurrentMonth),
  })

  useEffect(() => {
    const m = {}

    const t = startOfToday()
    setToday(t)

    slots?.map((slot) => {
      const d = parseISO(slot.start_time)
      if (isBefore(d, t)) return

      if (!m[d.getMonth()]) m[d.getMonth()] = {}
      if (!m[d.getMonth()][d.getDate()]) m[d.getMonth()][d.getDate()] = []
      m[d.getMonth()][d.getDate()].push(slot)
    })

    setMonths(m)
  }, [slots])

  useEffect(() => {
    const preloadNextMonth = async () => {
      const nextMonth = format(add(firstDayCurrentMonth, { months: 1 }), 'MM')
      const nextYear = format(add(firstDayCurrentMonth, { months: 1 }), 'yyyy')
      const preloadData = await preload(
        `/api/booking/slots/${step.id}/${nextYear}/${nextMonth}`,
        fetcher
      )
      setNextSlots(preloadData)
    }

    preloadNextMonth()
  }, [])

  function previousMonth() {
    let firstDayNextMonth = add(firstDayCurrentMonth, { months: -1 })
    setCurrentMonth(format(firstDayNextMonth, 'MMM-yyyy'))
  }

  function nextMonth() {
    let firstDayNextMonth = add(firstDayCurrentMonth, { months: 1 })
    const nextMonth = format(add(firstDayCurrentMonth, { months: 2 }), 'MM')
    const nextYear = format(add(firstDayCurrentMonth, { months: 2 }), 'yyyy')
    preload(`/api/booking/slots/${step.id}/${nextYear}/${nextMonth}`, fetcher)

    setCurrentMonth(format(firstDayNextMonth, 'MMM-yyyy'))
  }

  const createAppointment = async (data) => {
    const appointment: CreateBookingAppointmentInput = {}

    setSubmitting(true)

    appointment.slot = { id: isEditing.id }
    appointment.attributes = data
    appointment.service_step = step.id

    const res = await fetch('/api/booking/create', {
      method: 'POST',
      body: JSON.stringify(appointment),
    })

    const json = await res.json()

    if (!json.appointment) {
      setGlobalError(json.error)
      setSubmitting(false)
      return
    }

    setSubmitting(false)
    setIsEditing(false)
    setCreatedAppointment(json.appointment)
  }

  if (
    !months[selectedDay.getMonth()] ||
    !months[selectedDay.getMonth()][selectedDay.getDate()]
  ) {
    if (slots) {
      for (const slot of slots) {
        if (isBefore(parseISO(slot.start_time), selectedDay)) continue

        nextAvailableInterval = slot.start_time
        break
      }
    }

    if (!nextAvailableInterval && nextSlots) {
      for (const slot of nextSlots) {
        if (isBefore(parseISO(slot.start_time), selectedDay)) continue

        nextAvailableInterval = slot.start_time
        break
      }
    }
  }

  if (!step) {
    return <div>No step</div>
  }

  return (
    <Page>
      <Script
        src={`https://www.google.com/recaptcha/api.js?render=${process.env.NEXT_PUBLIC_RECAPTCHA_SITEKEY}`}
      />
      <Header menu={menu}></Header>
      <Wrap>
        {process.env.NEXT_PUBLIC_EVPERS == 'true' ||
          process.env.NEXT_PUBLIC_SERVICII == 'true' || (
            <Breadcrumbs>
              <Link href="/">Acasă</Link>
              <Link href="/servicii">Servicii</Link>
              <Link href={`/servicii/categorie/${step.service.category.slug}`}>
                {step.service.category.name}
              </Link>
              <Link href={`/servicii/${step.service.slug}`}>
                {step.service.name}
              </Link>
            </Breadcrumbs>
          )}
        {(process.env.NEXT_PUBLIC_EVPERS == 'true' ||
          process.env.NEXT_PUBLIC_SERVICII == 'true') && (
          <div className="mt-8"></div>
        )}
        {(!isEditing || isAuthenticated) && (
          <>
            <H1 className="">
              Programare {step.service.slug == 'casatorie' && 'depunere acte'}{' '}
              {step.service.name.toLowerCase()}{' '}
              {step.service.slug == 'act-identitate' && ' format vechi (1997)'}
            </H1>
            <Header.Description>
              <>
                {ReactHtmlParser(step.service.description, {
                  transform: handleHtmlNode,
                })}
              </>
            </Header.Description>
          </>
        )}
      </Wrap>

      <Main>
        {isEditing && !isAuthenticated ? (
          <div className="-mt-9">
            <LoginForm
              onLogin={() => {
                setIsAuthenticated(true)
              }}
              onCancel={() => {
                setIsEditing(false)
              }}
              profileFields={{
                cnp: null,
                email: { required: true },
              }}
            />
          </div>
        ) : createdAppointment ? (
          <>
            <div className="max-w-lg p-8 text-white bg-green-700">
              <h2 className="text-3xl font-bold border-0">
                Programare creată cu succes
              </h2>
              <div className="mt-8 text-xl text-left">
                Data:{' '}
                {format(
                  parseISO(createdAppointment.start_time),
                  'EEEE d MMMM yyy'
                )}
                <br />
                Ora: {format(parseISO(createdAppointment.start_time), 'HH:mm')}
                <br />
                Locație: {createdAppointment.slot.location.name}
              </div>
            </div>
            <div className="my-4">
              Pentru anularea programării, vizitați secțiunea{' '}
              <Link
                href="/cont"
                className="text-primary hover:text-primary-dark"
              >
                Contul meu
              </Link>
              .
            </div>
          </>
        ) : (
          <div className="-mt-4 sm:mt-0">
            <div className="max-w-md md:max-w-5xl">
              <div className="md:grid md:grid-cols-2 lg:grid-cols-5 md:divide-x md:divide-gray-200">
                <div className="md:pr-14 lg:col-span-2">
                  <div className="flex items-center pt-3" id="calendar">
                    <h2 className="flex-auto font-semibold text-gray-900 capitalize text-xl sm:text-2xl tracking-tight">
                      {format(firstDayCurrentMonth, 'MMMM yyyy')}
                    </h2>
                    <button
                      type="button"
                      onClick={previousMonth}
                      className="-my-1.5 flex flex-none items-center justify-center p-1.5 rounded-md text-primary hover:bg-primary hover:text-white"
                    >
                      <span className="sr-only">Previous month</span>
                      <ChevronLeftIcon className="w-8 h-8" aria-hidden="true" />
                    </button>
                    <button
                      onClick={nextMonth}
                      type="button"
                      className="-my-1.5 -mr-1.5 ml-2 flex flex-none items-center justify-center p-1.5 rounded-md text-primary hover:bg-primary hover:text-white"
                    >
                      <span className="sr-only">Next month</span>
                      <ChevronRightIcon
                        className="w-8 h-8"
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                  <div className="grid grid-cols-7 gap-2 mt-4 md:mt-6 leading-6 text-center font-semibold">
                    <div>Lu</div>
                    <div>Ma</div>
                    <div>Mi</div>
                    <div>Jo</div>
                    <div>Vi</div>
                    <div>Sâ</div>
                    <div>Du</div>
                  </div>
                  <div className="grid grid-cols-7 mt-2 gap-2">
                    {days.map((day, dayIdx) => (
                      <div
                        key={day.toString()}
                        className={classNames(
                          dayIdx === 0 && colStartClasses[getDay(day)],
                          ''
                        )}
                      >
                        <button
                          type="button"
                          onClick={(e) => {
                            const el = document.getElementById('calendar')
                            if (window.scrollY < el.offsetTop) {
                              el.scrollIntoView({
                                behavior: 'smooth',
                              })
                            }
                            setSelectedDay(day)
                            setIsEditing(null)
                          }}
                          className={classNames(
                            isSameDay(day, selectedDay) && 'text-white',
                            !isSameDay(day, selectedDay) &&
                              isSameDay(day, today) &&
                              'text-red-500',
                            months[day.getMonth()] &&
                              months[day.getMonth()][day.getDate()] &&
                              !isSameDay(day, selectedDay) &&
                              'text-primary font-bold border-2 border-primary hover:text-white hover:bg-primary',
                            (!today || !isSameDay(day, selectedDay)) &&
                              !isSameDay(day, today) &&
                              isSameMonth(day, firstDayCurrentMonth) &&
                              (!months[day.getMonth()] ||
                                !months[day.getMonth()][day.getDate()]) &&
                              'text-gray-300',
                            !isSameDay(day, selectedDay) &&
                              !isSameDay(day, today) &&
                              !isSameMonth(day, firstDayCurrentMonth) &&
                              'text-gray-400',
                            isSameDay(day, selectedDay) &&
                              isSameDay(day, today) &&
                              'bg-red-500',
                            isSameDay(day, selectedDay) &&
                              today &&
                              !isSameDay(day, today) &&
                              'bg-gray-900',
                            ((today && isSameDay(day, selectedDay)) ||
                              isSameDay(day, today)) &&
                              'font-semibold',
                            'mx-auto flex h-10 w-10 items-center justify-center rounded-lg'
                          )}
                        >
                          <time dateTime={format(day, 'yyyy-MM-dd')}>
                            {format(day, 'd')}
                          </time>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
                <section className="mt-12 md:mt-0 md:pl-14 lg:col-span-3">
                  <h2 className="font-semibold text-gray-900">
                    <time dateTime={format(selectedDay, 'yyyy-MM-dd')}>
                      <span className="capitalize">
                        {format(selectedDay, 'EEEE')}
                      </span>{' '}
                      {format(selectedDay, 'd MMMM yyy')}
                      {isEditing && (
                        <span>
                          , ora{' '}
                          {format(parseISO(isEditing.start_time), 'HH:mm')}
                        </span>
                      )}
                    </time>
                  </h2>

                  {isEditing
                    ? isAuthenticated && (
                        <form
                          onSubmit={handleSubmit(createAppointment)}
                          className="mb-8"
                        >
                          {step.service.slug == 'casatorie' && (
                            <div className="bg-amber-100 rounded-md p-4 mb-4">
                              <p>
                                Oficierea căsătoriei se poate face în termen de
                                11-30 zile de la data depunerii. Astfel, dacă
                                depuneți actele în{' '}
                                <b>{format(selectedDay, 'd MMMM')}</b>, veți
                                putea oficia căsătoria:
                              </p>
                              <ul className="list-disc list-inside -mt-4">
                                {getNextDays(selectedDay).map(
                                  (day) =>
                                    isHoliday(day) || (
                                      <li className="my-0">
                                        {format(day, 'EEEE')},{' '}
                                        {format(day, 'd MMMM yyy')}
                                      </li>
                                    )
                                )}
                              </ul>
                              <Button
                                small
                                onClick={() => {
                                  setIsEditing(null)
                                  document
                                    .getElementById('calendar')
                                    .scrollIntoView({
                                      behavior: 'smooth',
                                    })
                                }}
                              >
                                Alege altă dată
                              </Button>
                            </div>
                          )}
                          {globalError && (
                            <div className="bg-red-100 p-3 mb-2 text-red-800 rounded-md">
                              {globalError}
                            </div>
                          )}
                          <div className="bg-slate-100 px-4 pb-6 grid grid-cols-1 md:grid-cols-2 gap-x-4 border-b-4 border-primary">
                            {(() => {
                              const StaticFormComponent =
                                staticFormComponents[step.service.slug]
                              return StaticFormComponent ? (
                                <StaticFormComponent
                                  register={register}
                                  profile={profile}
                                  errors={errors}
                                  setValue={setValue}
                                />
                              ) : (
                                <DynamicForm
                                  fields={step.form.fields}
                                  register={register}
                                  profile={profile}
                                  errors={errors}
                                  setValue={setValue}
                                />
                              )
                            })()}
                          </div>
                          <div className="mt-4 flex flex-col sm:flex-row gap-4">
                            <Button
                              primary
                              disabled={submitting}
                              className="max-w-none"
                            >
                              {submitting && (
                                <svg
                                  className="w-5 h-5 mr-2 text-white animate-spin"
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx={12}
                                    cy={12}
                                    r={10}
                                    stroke="currentColor"
                                    strokeWidth={4}
                                  />
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                  />
                                </svg>
                              )}
                              Creează programarea
                            </Button>
                            <Button
                              className="max-w-none"
                              onClick={() => setIsEditing(null)}
                            >
                              Renunță
                            </Button>
                          </div>
                        </form>
                      )
                    : !createdAppointment && (
                        <ol className="-mt-2 text-gray-500 ml-0">
                          {months[selectedDay.getMonth()] &&
                          months[selectedDay.getMonth()][
                            selectedDay.getDate()
                          ] ? (
                            <div className="space-y-3">
                              {months[selectedDay.getMonth()][
                                selectedDay.getDate()
                              ].map((slot) => (
                                <Button
                                  small
                                  className="border-2 text-base w-20 mr-3"
                                  key={slot.start_time}
                                  onClick={() => {
                                    setIsEditing(slot)
                                  }}
                                >
                                  {format(parseISO(slot.start_time), 'HH:mm')}
                                </Button>
                              ))}
                            </div>
                          ) : (
                            <>
                              <p className="pt-2">
                                Niciun interval disponibil în această zi.
                              </p>
                              {nextAvailableInterval && (
                                <>
                                  <p className="mb-2">
                                    Următorul interval disponibil:{' '}
                                  </p>
                                  <p>
                                    <Button
                                      arrow
                                      primary
                                      onClick={(e) => {
                                        e.preventDefault()
                                        setCurrentMonth(
                                          format(
                                            parseISO(nextAvailableInterval),
                                            'MMM-yyyy'
                                          )
                                        )
                                        setSelectedDay(
                                          parseISO(nextAvailableInterval)
                                        )
                                      }}
                                    >
                                      <span className="capitalize">
                                        {format(
                                          parseISO(nextAvailableInterval),
                                          'EEEE'
                                        )}
                                      </span>{' '}
                                      {format(
                                        parseISO(nextAvailableInterval),
                                        'd MMMM yyy'
                                      )}
                                      , ora{' '}
                                      {format(
                                        parseISO(nextAvailableInterval),
                                        'HH:mm'
                                      )}
                                    </Button>
                                  </p>
                                </>
                              )}
                            </>
                          )}
                        </ol>
                      )}
                </section>
              </div>
            </div>
          </div>
        )}
      </Main>

      <Footer feedbackForm={false} />
    </Page>
  )
}

let colStartClasses = [
  'col-start-7',
  'col-start-1',
  'col-start-2',
  'col-start-3',
  'col-start-4',
  'col-start-5',
  'col-start-6',
]
